import Component from '@bythepixel/component-loader';
import Glide from '@glidejs/glide';
import '@glidejs/glide/dist/css/glide.core.min.css';

/**
 * Reviews slider for the Reviews + Ambassador Highlight block. The block is a
 * static 3-up grid at lg+ and a swipeable slider below it (the design turns the
 * review cards into a subtle peek-ahead carousel on mobile). Glide is therefore
 * mounted only while the mobile media query matches and destroyed above lg —
 * letting the cards fall back to the CSS grid — re-toggling across the boundary
 * on resize. One card has nothing to slide, so it stays static.
 *
 * Footer alignment: at lg+ the cards share grid tracks (CSS subgrid) so their
 * footers line up automatically. The mobile slider is a flex track, where
 * siblings can't size to each other, so we mirror the same result by giving
 * every card body the height of the tallest — a single measurement per
 * mount/resize (never per scroll-frame), cleared again above lg so the subgrid
 * is left untouched.
 */
const MOBILE_QUERY = '(max-width: 1023px)'; // below the `lg` (1024px) breakpoint
const BODY_SELECTOR = '.reviews-ambassador-highlight__card-body';

const GLIDE_OPTIONS = {
  type: 'slider',
  rewind: false,
  bound: true,
  perView: 1,
  gap: 16,
  peek: { before: 0, after: 48 }, // subtle peek of the next card
};

export default class ReviewsSlider extends Component {
  static selector = '[comp-reviews-slider]';

  #glide = null;

  #mql = null;

  #onChange = null;

  #onResize = null;

  #resizeRaf = 0;

  constructor(...args) {
    super(...args);
    this.init();
  }

  init() {
    const slides = this.$container.querySelectorAll('.glide__slide');

    // A single card has nothing to slide or align — leave it as the static grid.
    if (slides.length <= 1) return;

    this.#mql = window.matchMedia(MOBILE_QUERY);
    this.#onChange = () => this.sync();
    this.#mql.addEventListener('change', this.#onChange);

    // Re-measure on resize while the slider is active (orientation / width
    // changes reflow the quotes). Coalesced into one frame — runs on resize
    // only, never per scroll-frame.
    this.#onResize = () => {
      if (!this.#mql.matches) return;
      cancelAnimationFrame(this.#resizeRaf);
      this.#resizeRaf = requestAnimationFrame(() => this.equalizeBodies());
    };
    window.addEventListener('resize', this.#onResize);

    // Web-font swap changes text height after first paint — re-equalize once.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        if (this.#mql.matches) this.equalizeBodies();
      });
    }

    this.sync();
  }

  /** Mount Glide + equalise on mobile; destroy + clear (revert to the grid) above lg. */
  sync() {
    if (this.#mql.matches) {
      this.mountGlide();
      this.equalizeBodies();
    } else {
      this.destroyGlide();
      this.clearBodies();
    }
  }

  mountGlide() {
    if (this.#glide) return;

    this.#glide = new Glide(this.$container, GLIDE_OPTIONS);
    this.#glide.mount();
  }

  destroyGlide() {
    if (!this.#glide) return;

    this.#glide.destroy();
    this.#glide = null;
  }

  /**
   * Size every card body to the tallest so the footers align across slides.
   * Reset first so a re-measure reflects the current natural heights.
   */
  equalizeBodies() {
    const bodies = [...this.$container.querySelectorAll(BODY_SELECTOR)];

    if (bodies.length <= 1) return;

    bodies.forEach((body) => body.style.removeProperty('min-height'));

    const tallest = bodies.reduce((max, body) => Math.max(max, body.offsetHeight), 0);

    bodies.forEach((body) => body.style.setProperty('min-height', `${tallest}px`));
  }

  clearBodies() {
    this.$container
      .querySelectorAll(BODY_SELECTOR)
      .forEach((body) => body.style.removeProperty('min-height'));
  }

  destroy() {
    if (this.#mql && this.#onChange) {
      this.#mql.removeEventListener('change', this.#onChange);
    }

    if (this.#onResize) {
      window.removeEventListener('resize', this.#onResize);
    }

    cancelAnimationFrame(this.#resizeRaf);
    this.clearBodies();
    this.destroyGlide();
  }
}
